'use client';

import { text } from '@modules/garden-planner/text';
import { hasPlantImage } from '@modules/plants/business_logic';
import { text as gardenPlannerText } from '@modules/garden-planner/text_business_logic';
import React from 'react';

interface PlantedPlant {
  row: number;
  col: number;
  slot: number;
  plantId: string;
}

interface PlantMeta {
  id: string;
  name: string;
  emoji: string;
  imagePath?: string;
  speciesName?: string;
}

interface CellProps {
  row: number;
  col: number;
  plantsForCell: PlantedPlant[];
  getPlantById: (id: string) => PlantMeta | undefined;
  slots: number;
  isAvailable: boolean;
  isAvailabilityMode: boolean;
  isCellSettingsMode: boolean;
  isClearMode: boolean;
  isBulkSelected?: boolean;
  onClick: (row: number, col: number, event: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}

function getPlantLayout(plantCount: number) {
  switch (plantCount) {
    case 1:
      return [{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }];
    case 2:
      return [
        { top: '50%', left: '33%', transform: 'translate(-50%, -50%)' },
        { top: '50%', left: '67%', transform: 'translate(-50%, -50%)' },
      ];
    case 3:
      return [
        { top: '30%', left: '33%', transform: 'translate(-50%, -50%)' },
        { top: '30%', left: '67%', transform: 'translate(-50%, -50%)' },
        { top: '70%', left: '50%', transform: 'translate(-50%, -50%)' },
      ];
    case 4:
      return [
        { top: '35%', left: '33%', transform: 'translate(-50%, -50%)' },
        { top: '35%', left: '67%', transform: 'translate(-50%, -50%)' },
        { top: '65%', left: '33%', transform: 'translate(-50%, -50%)' },
        { top: '65%', left: '67%', transform: 'translate(-50%, -50%)' },
      ];
    case 5:
      return [
        { top: '25%', left: '25%', transform: 'translate(-50%, -50%)' },
        { top: '25%', left: '75%', transform: 'translate(-50%, -50%)' },
        { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
        { top: '75%', left: '25%', transform: 'translate(-50%, -50%)' },
        { top: '75%', left: '75%', transform: 'translate(-50%, -50%)' },
      ];
    default:
      return [];
  }
}

function getCellClassName(
  isAvailable: boolean,
  isAvailabilityMode: boolean,
  isClearMode: boolean,
  isBulkSelected: boolean,
  isFull: boolean,
  hasPlantsToClear: boolean
): string {
  const classes = ['garden-grid-cell'];

  if (isAvailabilityMode) {
    classes.push(
      isAvailable ? 'garden-grid-cell--availability-on' : 'garden-grid-cell--availability-off'
    );
    return classes.join(' ');
  }

  if (isClearMode) {
    if (isAvailable && hasPlantsToClear) {
      classes.push('garden-grid-cell--clear');
    } else if (!isAvailable) {
      classes.push('garden-grid-cell--unavailable');
    } else {
      classes.push('garden-grid-cell--clear');
    }
    return classes.join(' ');
  }

  if (isAvailable) {
    if (isBulkSelected) {
      classes.push('garden-grid-cell--bulk');
    } else {
      classes.push('garden-grid-cell--available');
      if (isFull) {
        classes.push('garden-grid-cell--full');
      }
    }
    return classes.join(' ');
  }

  classes.push('garden-grid-cell--unavailable');
  return classes.join(' ');
}

function Cell(props: CellProps) {
  const {
    row,
    col,
    plantsForCell,
    getPlantById,
    slots,
    isAvailable,
    isAvailabilityMode,
    isCellSettingsMode,
    isClearMode,
    isBulkSelected = false,
    onClick,
    onContextMenu,
  } = props;

  const plantLayout = getPlantLayout(plantsForCell.length);
  const isFull = plantsForCell.length >= slots;
  const plantedLabels = plantsForCell
    .map((plant) => {
      const plantData = getPlantById(plant.plantId);
      if (!plantData) return null;
      return gardenPlannerText.plantDisplayName(plantData.name, plantData.speciesName);
    })
    .filter((label): label is string => label !== null);

  const cellClassName = getCellClassName(
    isAvailable,
    isAvailabilityMode,
    isClearMode,
    isBulkSelected,
    isFull,
    plantsForCell.length > 0
  );

  const getTooltipText = () => {
    const cellLabel = text.cellLabel(row, col);

    if (isAvailabilityMode) {
      return `${cellLabel}\n${gardenPlannerText.cellAvailabilityClick(isAvailable)}`;
    }

    if (isClearMode) {
      if (isAvailable && plantsForCell.length > 0) {
        return text.cellClearAll(row, col, plantsForCell.length);
      }
      return text.cellNoPlantsToClear(row, col);
    }

    if (!isAvailable) {
      return text.cellNotAvailable(row, col);
    }

    if (plantedLabels.length > 0) {
      return gardenPlannerText.cellPlantedTooltip(row, col, plantedLabels);
    }

    return cellLabel;
  };

  return (
    <div
      className={cellClassName}
      style={{ width: 80, height: 80, minWidth: 80, minHeight: 80 }}
      title={getTooltipText()}
      onClick={(e) => onClick(row, col, e)}
      onContextMenu={(e) => {
        e.preventDefault();
        if (!isAvailabilityMode && !isCellSettingsMode) {
          onClick(row, col, e);
        }
        if (onContextMenu) onContextMenu(e);
      }}>
      {isAvailable ? <span className="garden-grid-cell__slot-count">{slots}</span> : null}

      {isBulkSelected && isAvailable ? (
        <span className="garden-grid-cell__bulk-check">✓</span>
      ) : null}

      {isAvailable
        ? plantsForCell.map((plant, index) => {
            const plantData = getPlantById(plant.plantId);
            const position = plantLayout[index];
            const plantLabel = plantData
              ? gardenPlannerText.plantDisplayName(plantData.name, plantData.speciesName)
              : null;

            return plantData && plantLabel ? (
              <div
                key={`${plant.slot}-${plant.plantId}`}
                className="garden-grid-cell__plant-slot"
                style={position}
                title={plantLabel}>
                <div className="garden-grid-cell__plant-icon">
                  {hasPlantImage(plantData.imagePath) ? (
                    <img
                      src={plantData.imagePath}
                      alt={plantLabel}
                      width={30}
                      height={30}
                      style={{ borderRadius: 9999 }}
                    />
                  ) : (
                    <span style={{ fontSize: 20, lineHeight: 1 }}>{plantData.emoji}</span>
                  )}
                </div>
              </div>
            ) : null;
          })
        : null}

      {isAvailabilityMode ? (
        <div className="garden-grid-cell__center">
          <span
            style={{
              fontSize: 24,
              color: isAvailable ? 'rgb(22 163 74)' : 'rgb(220 38 38)',
            }}>
            {isAvailable ? '✓' : '✕'}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function cellPropsAreEqual(prev: CellProps, next: CellProps): boolean {
  return (
    prev.row === next.row &&
    prev.col === next.col &&
    prev.plantsForCell === next.plantsForCell &&
    prev.getPlantById === next.getPlantById &&
    prev.onClick === next.onClick &&
    prev.slots === next.slots &&
    prev.isAvailable === next.isAvailable &&
    prev.isAvailabilityMode === next.isAvailabilityMode &&
    prev.isCellSettingsMode === next.isCellSettingsMode &&
    prev.isClearMode === next.isClearMode &&
    prev.isBulkSelected === next.isBulkSelected
  );
}

export default React.memo(Cell, cellPropsAreEqual);
